import isEqual from 'fast-deep-equal/es6/react';

/**
 * Generic props comparison function for React.memo
 *
 * This function automatically handles different types of props:
 * - Functions: Uses strict equality (===) since function references should be stable
 * - Everything else: Uses fastDeepEqual for deep comparison (primitives, objects, arrays, etc.)
 *
 * @param prevProps - Previous props object
 * @param nextProps - Next props object
 * @returns true to SKIP re-render (props are equal), false to re-render (props changed)
 *
 * @example
 * ```typescript
 * export const MyComponent = memo(MyComponentImpl, arePropsEqual);
 * ```
 */
export function arePropsEqual<T extends Record<string, any>>(
  prevProps: T,
  nextProps: T
): boolean {
  const prevKeys = Object.keys(prevProps) as (keyof T)[];
  const nextKeys = Object.keys(nextProps) as (keyof T)[];

  if (prevKeys === nextKeys) {
    return true;
  }

  if (prevKeys.length !== nextKeys.length) {
    return false;
  }

  for (const key of prevKeys) {
    const prevValue = prevProps[key];
    const nextValue = nextProps[key];

    if (typeof prevValue === 'function' || typeof nextValue === 'function') {
      if (prevValue !== nextValue) {
        return false;
      }
    } else {
      if (!isEqual(prevValue, nextValue)) {
        return false;
      }
    }
  }

  return true;
}
